// Single source of truth for the BI-Asset-* field tags.
//
// Every consumer derives from this list: the tag-pivot request in App.vue, the
// typed projection in assetModel.js, the completeness KPI/chart, the grid
// presets and the `hasAssetField` store getter. Add a tag here and it flows
// everywhere; nothing else in the app spells a "BI-Asset-*" string.
//
//   tag          the RDrive field tag as returned by the tag-pivot endpoint
//   alias        the column name exposed on asset_items / unique_assets
//   kind         drives the SQL cast: text | date | number | image | bool
//   labelKey     i18n key for the column / bar label
//   required     part of the asset's identity (Register Completeness KPI)
//   register     counted in the Asset Data Completeness visual
//   perItem      captured per lifecycle record (inspection data), not per asset
export const ASSET_FIELDS = [
  { tag: 'BI-Asset-ID', alias: 'assetRefId', kind: 'text', labelKey: 'assetId', required: true },
  { tag: 'BI-Asset-Category', alias: 'assetCategory', kind: 'text', labelKey: 'category', required: true, register: true },
  { tag: 'BI-Asset-System', alias: 'assetSystem', kind: 'text', labelKey: 'assetSystem', required: true, register: true },
  { tag: 'BI-Asset-Type', alias: 'assetType', kind: 'text', labelKey: 'assetType', required: true, register: true },
  { tag: 'BI-Asset-Manufacturer', alias: 'manufacturer', kind: 'text', labelKey: 'manufacturer', register: true },
  { tag: 'BI-Asset-Model', alias: 'model', kind: 'text', labelKey: 'model', register: true },
  { tag: 'BI-Asset-Serial-Number', alias: 'serialNumber', kind: 'text', labelKey: 'serialNumber', register: true },
  { tag: 'BI-Asset-Install-Date', alias: 'installDate', kind: 'date', labelKey: 'installDate' },
  { tag: 'BI-Asset-Commission-Date', alias: 'commissionDate', kind: 'date', labelKey: 'commissionDate', register: true },
  { tag: 'BI-Asset-Photo', alias: 'photo', kind: 'image', labelKey: 'photo', register: true },
  { tag: 'BI-Asset-Response-Photo', alias: 'responsePhoto', kind: 'image', labelKey: 'responsePhoto' },
  { tag: 'BI-Asset-Signature', alias: 'signature', kind: 'image', labelKey: 'signature', register: true },
  { tag: 'BI-Asset-QR-Code', alias: 'qrCode', kind: 'text', labelKey: 'qrCode', register: true },
  { tag: 'BI-Asset-Inspection-Date', alias: 'inspectionDate', kind: 'date', labelKey: 'inspectionDate', perItem: true },
  { tag: 'BI-Asset-Next-Inspection-Date', alias: 'nextInspectionDate', kind: 'date', labelKey: 'nextInspection', perItem: true },
  { tag: 'BI-Asset-Installation-Cost', alias: 'installationCost', kind: 'number', labelKey: 'installationCost', register: true },
  { tag: 'BI-Asset-Rating', alias: 'rating', kind: 'text', labelKey: 'rating', perItem: true },
  { tag: 'BI-Asset-PassRate', alias: 'passRate', kind: 'number', labelKey: 'passRate', perItem: true },
  { tag: 'BI-Asset-Responsible-Party', alias: 'responsibleParty', kind: 'text', labelKey: 'responsibleParty' },
  { tag: 'BI-Asset-Priority', alias: 'priority', kind: 'bool', labelKey: 'priority' },
  { tag: 'BI-Asset-Quantity', alias: 'quantity', kind: 'number', labelKey: 'quantity' },
  { tag: 'BI-Asset-Description', alias: 'assetDescription', kind: 'text', labelKey: 'description' },
  { tag: 'BI-Asset-Comments', alias: 'comments', kind: 'text', labelKey: 'comments' },
];

export const REGISTER_FIELDS = ASSET_FIELDS.filter((field) => field.register);
export const REQUIRED_FIELDS = ASSET_FIELDS.filter((field) => field.required);

export const fieldByAlias = (alias) => ASSET_FIELDS.find((field) => field.alias === alias) || null;

// Query-string fragment for the tag-pivot endpoint.
export const fieldTagsQuery = () => ASSET_FIELDS.map((field) => `fieldTags=${field.tag}`).join('&');

// SQL expression that normalises a raw pivot column (VARCHAR on the live API,
// but read_json_auto may already have inferred DATE/BIGINT/BOOLEAN for demo
// data) into the alias' canonical type. Blank strings become NULL so they
// never count as "populated".
export function castExpr(kind, ref) {
  const text = `NULLIF(TRIM(CAST(${ref} AS VARCHAR)), '')`;
  switch (kind) {
    case 'date':
      // ISO first, then the day-first formats RDrive emits for some locales.
      return `CAST(COALESCE(
        TRY_CAST(${text} AS TIMESTAMP),
        TRY_STRPTIME(${text}, '%d/%m/%Y %H:%M:%S'),
        TRY_STRPTIME(${text}, '%d/%m/%Y %H:%M'),
        TRY_STRPTIME(${text}, '%d/%m/%Y')
      ) AS DATE)`;
    case 'number':
      return `TRY_CAST(regexp_replace(${text}, '[^0-9.\\-]', '', 'g') AS DOUBLE)`;
    case 'bool':
      return `CASE LOWER(${text})
        WHEN 'true' THEN TRUE WHEN '1' THEN TRUE WHEN 'yes' THEN TRUE
        WHEN 'false' THEN FALSE WHEN '0' THEN FALSE WHEN 'no' THEN FALSE
      END`;
    case 'image':
      return `NULLIF(CAST(${ref} AS VARCHAR), '')`;
    default:
      return text;
  }
}

// Typed NULL for a tag the project has not configured, so the model tables
// always carry every alias with a stable type.
export function nullExpr(kind) {
  switch (kind) {
    case 'date': return 'CAST(NULL AS DATE)';
    case 'number': return 'CAST(NULL AS DOUBLE)';
    case 'bool': return 'CAST(NULL AS BOOLEAN)';
    default: return 'CAST(NULL AS VARCHAR)';
  }
}

// Quote a JS value as a SQL string literal. Every interpolated user/data value
// goes through here - demo data strips apostrophes, live data will not.
export const sqlLiteral = (value) => `'${String(value ?? '').replace(/'/g, "''")}'`;
